import { useEffect, useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import QRCode from 'qrcode';
import {
  api,
  errorMessage,
  ENTITY_STATUSES,
  ENTITY_STATUS_LABELS,
  type AdminQrCode,
  type EntityStatus,
} from '@/lib/api';
import { cn } from '@/lib/cn';

// Visitor URL the QR sticker actually encodes. The mobile static site is on
// the apex domain, so we point printed stickers there. `?` is intentionally
// absent — keeping it as a clean path means QR encoders use less data and
// the printed dots stay easier to scan on cheap paper.
const VISITOR_BASE_URL =
  import.meta.env.VITE_VISITOR_BASE_URL ?? 'https://explorentia.eu';

export const Route = createFileRoute('/_authed/qr-codes/$qrId')({
  component: QrCodeDetailPage,
});

const editSchema = z.object({
  name: z.string().min(1).max(120),
  status: z.enum(ENTITY_STATUSES as [EntityStatus, ...EntityStatus[]]),
  internalNotes: z.string().nullable().optional(),
});

type EditValues = z.infer<typeof editSchema>;

function QrCodeDetailPage() {
  const { qrId } = Route.useParams();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['qr-code', qrId],
    queryFn: () => api.getQrCode(qrId),
  });

  const save = useMutation({
    mutationFn: (body: Partial<AdminQrCode>) => api.updateQrCode(qrId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-code', qrId] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });

  const archive = useMutation({
    mutationFn: () => api.archiveQrCode(qrId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-code', qrId] });
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });

  if (q.isLoading || !q.data) return <div className="card text-slate-gray">Loading…</div>;

  const { qrCode, stats } = q.data;
  const visitorUrl = `${VISITOR_BASE_URL}/${qrCode.code}`;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/qr-codes" className="btn-ghost -ml-3 mb-2">
          ← Back to QR codes
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-h1 font-mono">{qrCode.code}</h1>
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-caption font-semibold',
              qrCode.status === 'active' && 'bg-success/15 text-success',
              qrCode.status === 'inactive' && 'bg-warning/15 text-warning',
              qrCode.status === 'archived' && 'bg-light-gray text-slate-gray',
            )}
          >
            {ENTITY_STATUS_LABELS[qrCode.status]}
          </span>
          <span className="rounded-full bg-tech-blue/10 px-2.5 py-0.5 text-caption font-semibold text-tech-blue">
            {stats.scans} scans
          </span>
        </div>
        <p className="mt-1 text-caption text-slate-gray">
          Visitor URL:{' '}
          <a href={visitorUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-tech-blue hover:underline">
            {visitorUrl}
          </a>
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr,18rem]">
        <EditForm
          qrCode={qrCode}
          onSubmit={(v) => save.mutateAsync(v)}
          saving={save.isPending}
          saved={save.isSuccess}
          error={save.error ? errorMessage(save.error) : null}
          onArchive={() => archive.mutateAsync()}
          archiving={archive.isPending}
        />
        <QrPreview url={visitorUrl} code={qrCode.code} />
      </div>
    </div>
  );
}

function EditForm({
  qrCode,
  onSubmit,
  saving,
  saved,
  error,
  onArchive,
  archiving,
}: {
  qrCode: AdminQrCode;
  onSubmit: (v: EditValues) => Promise<unknown>;
  saving: boolean;
  saved: boolean;
  error: string | null;
  onArchive: () => void;
  archiving: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: qrCode.name,
      status: qrCode.status,
      internalNotes: qrCode.internalNotes,
    },
  });

  const submit = async (v: EditValues) => {
    await onSubmit({ ...v, internalNotes: v.internalNotes || null });
    reset(v);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-5">
      <h2 className="text-h2">Sticker metadata</h2>

      <div>
        <label className="label">Admin name</label>
        <input className="input" {...register('name')} />
      </div>

      <div>
        <label className="label">Status</label>
        <select className="input" {...register('status')}>
          {ENTITY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {ENTITY_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-caption text-slate-gray">
          <strong>Inactive</strong> shows visitors a "currently unavailable"
          screen.{' '}
          <strong>Archived</strong> 404s; reserved for stickers retired from
          circulation.
        </p>
      </div>

      <div>
        <label className="label">Internal notes</label>
        <textarea className="input min-h-[5rem]" rows={3} {...register('internalNotes')} />
      </div>

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className="btn-primary" disabled={!isDirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && !isDirty ? (
          <span className="text-caption text-success">Saved ✓</span>
        ) : null}
        {qrCode.status !== 'archived' ? (
          <button
            type="button"
            className="btn-ghost text-error hover:bg-error/10"
            onClick={onArchive}
            disabled={archiving}
          >
            {archiving ? 'Archiving…' : 'Archive sticker'}
          </button>
        ) : null}
      </div>
    </form>
  );
}

function QrPreview({ url, code }: { url: string; code: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [pngDataUrl, setPngDataUrl] = useState<string | null>(null);

  // Render to canvas for on-screen preview, and prepare a separate
  // higher-resolution data URL for the "Download PNG" anchor. The download
  // uses 1024px to look sharp on stickers up to ~10cm; the preview is a
  // lighter 240px because that's what fits in the sidebar.
  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 240,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#1A2A3A', light: '#FFFFFF' },
      }).catch((err) => console.error('QR render failed', err));
    }
    QRCode.toDataURL(url, {
      width: 1024,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then(setPngDataUrl)
      .catch((err) => console.error('QR data URL failed', err));
  }, [url]);

  return (
    <aside className="card flex flex-col items-center gap-3">
      <h3 className="text-h2">Preview</h3>
      <canvas ref={canvasRef} className="rounded-lg" />
      <p className="break-all text-center text-caption text-slate-gray">{url}</p>
      {pngDataUrl ? (
        <a
          href={pngDataUrl}
          download={`explorentia-${code}.png`}
          className="btn-primary w-full text-center"
        >
          Download PNG (1024px)
        </a>
      ) : null}
      <p className="text-caption text-slate-gray">
        Error correction: M. Safe for stickers with up to ~15% obscured area.
      </p>
    </aside>
  );
}
